import createClient from "openapi-fetch"
import {
	HTMLLoginFormElement,
	LoginFormLoginEvent,
} from "./custom-elements/html-login-form-element.js"
import {
	HTMLTimelineAdminElement,
	LoadingError,
} from "./custom-elements/html-timeline-admin-element.js"
import {
	HTMLUsernameElement,
	StatusChangeEvent as UsernameStatusChangeEvent,
} from "./custom-elements/html-username-element.js"
import "./custom-elements/html-app-version-element.js"
import { env } from "./lib/env.js"
import { getElement } from "./lib/get-elements.js"
import "iconify-icon"
import type { paths } from "@lightmill/log-api"
import { HTMLLoadingButtonElement } from "./custom-elements/html-loading-button-element.js"

try {
	const adminElement = getElement("#admin", document, HTMLTimelineAdminElement)
	const loginDialogElement = getElement(
		"#login-dialog",
		document,
		HTMLDialogElement,
	)
	const loginFormElement = getElement(
		"x-login-form",
		loginDialogElement,
		HTMLLoginFormElement,
	)
	const usernameElement = getElement("#username", document, HTMLUsernameElement)
	const bodyElement = getElement("body", document, HTMLBodyElement)
	const loginButtonElement = getElement(
		"#login-button",
		document,
		HTMLLoadingButtonElement,
	)
	const logoutButtonElement = getElement(
		"#logout-button",
		document,
		HTMLLoadingButtonElement,
	)

	bodyElement.dataset.timelineStatus = "idle"
	bodyElement.dataset.usernameStatus = "idle"

	loginFormElement.addEventListener("login", (event) => {
		if (!(event instanceof LoginFormLoginEvent)) return
		loginDialogElement.close()
		usernameElement.set({ username: event.detail.username })
		bodyElement.dataset.timelineStatus = "loading"
		adminElement.reload()
	})

	loginFormElement.addEventListener("error", handleError)
	loginDialogElement.addEventListener("error", handleError)
	adminElement.addEventListener("error", (event) => {
		if (
			event.type === "error" &&
			event.error instanceof LoadingError &&
			event.error.message.includes("SESSION_REQUIRED")
		) {
			return
		}
		handleError(event.error)
	})

	adminElement.addEventListener("load", () => {
		bodyElement.dataset.timelineStatus = "ready"
	})

	window.addEventListener("pageshow", (event) => {
		if (event.persisted) {
			// Reload the timelines if the page was loaded from the bfcache.
			adminElement.reload()
		}
	})

	usernameElement.addEventListener(
		UsernameStatusChangeEvent.eventName,
		(event) => {
			if (!(event instanceof UsernameStatusChangeEvent)) return
			bodyElement.dataset.usernameStatus = event.detail.status
			if (event.detail.status === "ready" && event.detail.username === null) {
				bodyElement.dataset.timelineStatus = "idle"
				loginDialogElement.showModal()
			}
		},
	)

	logoutButtonElement.addEventListener("click", async () => {
		let client = createClient<paths>({ baseUrl: env.logApiUrl })
		logoutButtonElement.loading = true
		client
			.DELETE("/sessions/{id}", {
				params: { path: { id: "current" } },
				credentials: "include",
			})
			.then((response) => {
				if (response.error) {
					let error = response.error.errors[0]
					handleError(error.detail ?? error.code)
					return
				}
				usernameElement.set({ username: null })
				bodyElement.dataset.timelineStatus = "idle"
				loginDialogElement.showModal()
			})
			.finally(() => {
				logoutButtonElement.loading = false
			})
			.catch(handleError)
	})

	loginButtonElement.addEventListener("click", () => {
		loginDialogElement.showModal()
	})
	getElement("#login-link", document, HTMLAnchorElement).addEventListener(
		"click",
		(event) => {
			event.preventDefault()
			loginDialogElement.showModal()
		},
	)

	bodyElement.dataset.timelineStatus = "loading"
	bodyElement.dataset.usernameStatus = "loading"
	adminElement.timelinesSrc = env.timelineApiUrl
	adminElement.logSrc = env.logApiUrl
	loginFormElement.apiSrc = env.logApiUrl
	usernameElement.apiSrc = env.logApiUrl
} catch (error) {
	handleError(error)
}

function handleError(error: unknown) {
	// biome-ignore lint/suspicious/noConsole: This is on purpose.
	console.error(error)
	document.body.innerHTML = `
		<main>
			<h2>An error occurred...</h2>
			<div>
				<code><pre>${error instanceof Error ? error.stack : String(error)}</pre></code>
			</div>
			<p>Please try reloading the page.</p>
		</main>
	`
	document.body.classList.add("crashed")
}
